import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Icons,
  Modal,
  Stack,
  styled,
  Text,
  NumericInput,
  InputLabel,
} from '@/theme/index';
import { useLocationInterface } from '@/utils/hooks/useLocationInterface/useLocationInterface';
import { schemaVehicleDetailForm } from '@/utils/schemaForms/schemaForms';
import { UpdateTrackedVehicleDetailParams } from '../oramInterfaces';

export interface VehicleDetailUpdateModalProps {
  /** If true, the modal is modalOpen. */
  isModalOpen: boolean;
  /** Callback fired when the user tries to close the modal by either clicking on the backdrop or pressing the escape key */
  closeModal: () => void;
  /** If true, the modal is modalOpen. */
  vehicleYMM: string;
  /** Callback to update Oram detail */
  handleDetailMutation: (data: UpdateTrackedVehicleDetailParams) => void;
  /** Callback to update Oram detail */
  oramData: {
    mileage: number | null;
    averageMileage: number | null;
    zip: string | null;
    tracked_vehicle_id: number | null;
  };
}

const StyledModal = styled(Modal)`
  .MuiDialog-paper {
    width: 600px;
  }
`;

const StyledText = styled(Text)`
  cursor: pointer;
  text-decoration: underline;
  color: ${({ theme }) => theme.palette.primary.main};
`;

/**
 * This component is a generic modal used to display the claim vehicle form
 */
export const VehicleDetailUpdateModal: React.FC<
  VehicleDetailUpdateModalProps
> = ({
  isModalOpen,
  closeModal,
  vehicleYMM,
  handleDetailMutation,
  oramData,
}) => {
  const {
    handleSubmit,
    register,
    formState,
    getFieldState,
    getValues,
    setError,
    reset,
  } = useForm<{
    currentMileage: number | null;
    yearlyMileage: number | null;
    zipCode: string | null;
  }>({
    defaultValues: {
      currentMileage: oramData.mileage,
      yearlyMileage: oramData.averageMileage,
      zipCode: oramData.zip,
    },
    mode: 'onTouched',
    resolver: yupResolver(schemaVehicleDetailForm),
  });
  const [submitted, setSubmitted] = useState(false);
  const zipCode = getValues('zipCode');
  const { data: locationData, isLoading } = useLocationInterface(
    zipCode || '',
    submitted
  );

  useEffect(() => {
    reset(() => ({
      currentMileage: oramData.mileage,
      yearlyMileage: oramData.averageMileage,
      zipCode: oramData.zip,
    }));
  }, [oramData.mileage, oramData.averageMileage, oramData.zip]);

  const handleFormSubmit = () => {
    const data = getValues();
    if (!oramData.tracked_vehicle_id) return;

    handleDetailMutation({
      mileage: data.currentMileage,
      average_mileage: data.yearlyMileage,
      city: locationData.city,
      state: locationData.state,
      zip: locationData.zip5,
      source: 'user',
      tracked_vehicle_id: oramData.tracked_vehicle_id,
    });

    setSubmitted(false);
    closeModal();
  };

  useEffect(() => {
    if (submitted && !isLoading && !locationData?.city) {
      setError('zipCode', {
        type: 'manual',
        message: 'Please enter a valid zip code',
      });
      setSubmitted(false);
    }

    if (locationData?.city && locationData?.state && locationData?.zip5) {
      handleFormSubmit();
    }
  }, [submitted, locationData, isLoading]);

  useEffect(() => {
    if (isModalOpen) {
      reset(() => ({
        currentMileage: oramData.mileage,
        yearlyMileage: oramData.averageMileage,
        zipCode: oramData.zip,
      }));
    }
  }, [isModalOpen]);

  return (
    <StyledModal open={isModalOpen} onClose={closeModal}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Text variant="h3">Milage and Location for {vehicleYMM}</Text>
        <IconButton disableRipple onClick={closeModal} sx={{ mb: 1.5 }}>
          <Icons.Close fontSize="small" color="disabled" />
        </IconButton>
      </Stack>
      <Box
        component="form"
        onSubmit={handleSubmit(() => {
          setSubmitted(true);
        })}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <InputLabel
              htmlFor="currentMileage"
              sx={{ fontSize: '1rem', fontWeight: 700, color: 'black' }}
            >
              Current Mileage
            </InputLabel>
            <NumericInput
              {...register('currentMileage', { valueAsNumber: true })}
              name="currentMileage"
              variant="outlined"
              value={getValues('currentMileage')}
              fullWidth
              error={getFieldState('currentMileage').invalid}
            />
            {formState.errors.currentMileage?.message}
          </Grid>
          <Grid item xs={12} md={6}>
            <InputLabel
              htmlFor="yearlyMileage"
              sx={{ fontSize: '1rem', fontWeight: 700, color: 'black' }}
            >
              Approximate yearly mileage
            </InputLabel>
            <NumericInput
              {...register('yearlyMileage', { valueAsNumber: true })}
              name="yearlyMileage"
              variant="outlined"
              value={getValues('yearlyMileage')}
              fullWidth
              error={getFieldState('yearlyMileage').invalid}
            />
            {formState.errors.yearlyMileage?.message}
          </Grid>
          <Grid item xs={12} md={6}>
            <InputLabel
              htmlFor="Location"
              sx={{ fontSize: '1rem', fontWeight: 700, color: 'black' }}
            >
              Zip Code
            </InputLabel>
            <NumericInput
              {...register('zipCode')}
              name="zipCode"
              variant="outlined"
              value={getValues('zipCode')}
              fullWidth
              error={getFieldState('zipCode').invalid}
            />
            {formState.errors.zipCode?.message}
          </Grid>
        </Grid>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ mt: 2 }}
        >
          <StyledText
            variant="body2"
            sx={{ alignSelf: 'center' }}
            onClick={closeModal}
          >
            Cancel
          </StyledText>
          <Button
            variant="contained"
            size="small"
            type="submit"
            color="success"
          >
            Update
          </Button>
        </Stack>
      </Box>
    </StyledModal>
  );
};
